import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "./client";
import { useAuthStore } from "@/store/authStore";

/**
 * Onboarding progress + welcome modal + product-tour state +
 * coachmark dismissals (spec 23). Backed by the /me/* endpoints
 * shipped in monitoring-backend p5_23 — see app/routers/me.py.
 *
 * **Scoping**: every endpoint is strictly self — the user_id always
 * comes from the Cognito ID token, never a path / query param. So the
 * queryKeys do NOT need actingAs.clientId (CROSS_PAGE_DECISIONS §13
 * applies to tenant-scoped data — onboarding state is per-user not
 * per-tenant). They DO key on `previewPersona` so a SuperAdmin
 * flipping persona-preview gets a fresh response.
 */

export type OnboardingPersona =
  | "superadmin"
  | "clientadmin"
  | "om"
  | "siteowner";

export type StepId =
  | "profile"
  | "mfa"
  | "triage_inbox"
  | "walk_fleet"
  | "ack_alarm"
  | "generate_report"
  | "add_customer"
  | "register_site"
  | "invite_team"
  | "configure_branding"
  | "onboard_first_client"
  | "platform_defaults"
  | "explore_my_plant"
  | "set_goal"
  | "subscribe_statements"
  | "contact_support";

export interface OnboardingStepCta {
  label: string;
  href: string;
}

export interface OnboardingStep {
  id: StepId;
  title: string;
  description: string;
  done: boolean;
  recommended: boolean;
  blocked: boolean;
  cta: OnboardingStepCta;
}

export interface OnboardingProgressResponse {
  persona: OnboardingPersona;
  steps: OnboardingStep[];
  percent: number;
  last_updated: string;
  welcome_dismissed: boolean;
  onboarding_reset_at: string | null;
}

export interface WelcomeStateResponse {
  welcome_dismissed: boolean;
  welcome_dismissed_at: string | null;
}

/**
 * GET /me/onboarding-progress[?preview_persona=…]
 *
 * 5-min staleTime + refetch-on-focus matches spec 23 §6.2. Backend
 * silently ignores `preview_persona` for non-SuperAdmin (defence in
 * depth on both sides) — the UI control that surfaces the dropdown is
 * already SuperAdmin-only.
 */
export function useOnboardingProgress(
  options: { previewPersona?: OnboardingPersona | null } = {},
) {
  const { previewPersona } = options;
  return useQuery<OnboardingProgressResponse>({
    queryKey: ["onboarding-progress", previewPersona ?? "self"],
    queryFn: async () => {
      const params = previewPersona
        ? { preview_persona: previewPersona }
        : undefined;
      const r = await apiClient.get<OnboardingProgressResponse>(
        "/me/onboarding-progress",
        {
          params,
        },
      );
      return r.data;
    },
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: true,
  });
}

/**
 * POST /me/onboarding-progress/reset — clears progress, welcome
 * dismissal, tour state, and seen features. Audit-logged server-side.
 * Invalidates every onboarding queryKey so the page re-fetches with
 * the fresh state without an explicit reload.
 */
export function useResetOnboarding() {
  const qc = useQueryClient();
  return useMutation<OnboardingProgressResponse, Error, void>({
    mutationFn: async () => {
      const r = await apiClient.post<OnboardingProgressResponse>(
        "/me/onboarding-progress/reset",
      );
      return r.data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["onboarding-progress"] });
      void qc.invalidateQueries({ queryKey: ["welcome-state"] });
      void qc.invalidateQueries({ queryKey: ["tour-state"] });
      void qc.invalidateQueries({ queryKey: ["seen-features"] });
    },
  });
}

/**
 * POST /me/welcome/dismiss — idempotent stamp. Used by the WelcomeModal
 * "Skip for now" / "Take the tour" buttons to remove the modal from
 * future sessions. The deeplink ?welcome=1 bypasses the dismissed flag
 * client-side for re-test.
 */
export function useDismissWelcome() {
  const qc = useQueryClient();
  return useMutation<WelcomeStateResponse, Error, void>({
    mutationFn: async () => {
      const r = await apiClient.post<WelcomeStateResponse>(
        "/me/welcome/dismiss",
      );
      return r.data;
    },
    onSuccess: (data) => {
      qc.setQueryData(["welcome-state"], data);
      // Progress payload also carries `welcome_dismissed` — refetch so
      // the GettingStartedPage banner state stays consistent.
      void qc.invalidateQueries({ queryKey: ["onboarding-progress"] });
    },
  });
}

// ---------------------------------------------------------------------- //
// Coachmark / first-visit tip dismissals (user_seen_features)
// ---------------------------------------------------------------------- //

export interface SeenFeatureRow {
  feature_key: string;
  dismissed_at: string;
}

/**
 * GET /me/seen-features. Used by the Coachmark primitive to decide
 * whether to render its tip. Cached aggressively — 30 min staleTime —
 * because dismissals are write-then-read and we already invalidate
 * after every POST/DELETE.
 */
export function useSeenFeatures() {
  return useQuery<SeenFeatureRow[]>({
    queryKey: ["seen-features"],
    queryFn: async () =>
      (await apiClient.get<SeenFeatureRow[]>("/me/seen-features")).data,
    staleTime: 30 * 60 * 1000,
  });
}

export function useDismissFeature() {
  const qc = useQueryClient();
  return useMutation<SeenFeatureRow, Error, string>({
    mutationFn: async (featureKey) => {
      const r = await apiClient.post<SeenFeatureRow>("/me/seen-features", {
        feature_key: featureKey,
      });
      return r.data;
    },
    onSuccess: (row) => {
      // Append-or-replace — keeps the React Query cache hot without a
      // refetch, matching the "tip disappears immediately" UX.
      qc.setQueryData<SeenFeatureRow[]>(["seen-features"], (prev) => {
        const without = (prev ?? []).filter(
          (r) => r.feature_key !== row.feature_key,
        );
        return [...without, row];
      });
    },
  });
}

export function useResetCoachmarks() {
  const qc = useQueryClient();
  return useMutation<void, Error, void>({
    mutationFn: async () => {
      await apiClient.delete("/me/seen-features");
    },
    onSuccess: () => {
      qc.setQueryData<SeenFeatureRow[]>(["seen-features"], []);
    },
  });
}

/**
 * Re-export of the auth-store `actingAs` selector. The Coachmark hook
 * uses it for parity with API-key inventory expectations (every shared
 * hook is reviewable for the actingAs.clientId check). Onboarding hooks
 * are intentionally NOT keyed on actingAs because the data is per-user,
 * but exposing the selector keeps the cross-reference visible.
 */
export const useActingAsForReview = () =>
  useAuthStore((s) => s.actingAs?.clientId ?? null);
